import React from "react";
import {
  Avatar,
  Box,
  Chip,
  CircularProgress,
  Stack,
  Typography,
} from "@mui/material";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { asyncStatus } from "../../../utils/asyncStatus";
import { themeOrange } from "../../../utils/colorTheme";
import { format } from "date-fns";
import { MdCheckCircle, MdArrowForward, MdAccessTime, MdLocationOn, MdEmail, MdOutlineWork } from "react-icons/md";
import { FaCalendarAlt, FaStar } from "react-icons/fa";
import { getServiceLocalImage } from "../../../utils/serviceImages";

const CompletedVendorJobsTab = () => {
  const navigate = useNavigate();
  const { get_job_by_vendor_status, get_job_by_vendor_data } = useSelector(
    (state) => state.serviceManage
  );

  const jobDoneLoader = get_job_by_vendor_status === asyncStatus.LOADING;
  const completedJobs = get_job_by_vendor_data?.filter(
    (job) => job.status === "Completed" || job.status === "Aprroval-Waiting"
  ) || [];

  const parseDate = (dateStr) => {
    if (!dateStr || typeof dateStr !== "string") return null;
    const parts = dateStr.split("-");
    if (parts.length !== 3) return null;
    const [year, month, day] = parts;
    const d = new Date(year, month - 1, day);
    return isNaN(d.getTime()) ? null : d;
  };

  const formatDate = (dateStr) => {
    const d = parseDate(dateStr);
    if (!d) return null;
    return format(d, "EEE, MMM d, yyyy");
  };

  const isWaiting = (status) => status === "Aprroval-Waiting";

  if (jobDoneLoader) {
    return (
      <Stack alignItems="center" justifyContent="center" sx={{ height: "60vh" }}>
        <CircularProgress size={30} sx={{ color: themeOrange }} />
      </Stack>
    );
  }

  if (!completedJobs.length) {
    return (
      <Stack alignItems="center" justifyContent="center" sx={{ height: "60vh" }} spacing={2}>
        <Box
          sx={{
            width: 80,
            height: 80,
            borderRadius: "50%",
            backgroundColor: "#f5f5f5",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <MdOutlineWork size={36} color="#ccc" />
        </Box>
        <Typography sx={{ color: "#aaa", fontWeight: "500", fontSize: "16px" }}>
          No completed jobs yet
        </Typography>
      </Stack>
    );
  }

  return (
    <Stack spacing={0}>
      {/* Summary bar */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 3 }}
      >
        <Stack direction="row" alignItems="center" gap={1}>
          <Typography sx={{ fontWeight: "700", fontSize: "20px", color: "#1a1a1a" }}>
            {completedJobs.length}
          </Typography>
          <Typography sx={{ color: "#888", fontSize: "14px" }}>
            completed {completedJobs.length === 1 ? "job" : "jobs"}
          </Typography>
        </Stack>
        <Chip
          label="Completed"
          size="small"
          icon={<MdCheckCircle size={14} color="#2e7d32" />}
          sx={{
            backgroundColor: "#e8f5e9",
            color: "#2e7d32",
            fontWeight: "600",
            fontSize: "12px",
            border: "1px solid #a5d6a7",
          }}
        />
      </Stack>

      {/* Job Cards */}
      <Stack spacing={2}>
        {completedJobs.map((job) => {
          const customerName = `${job?.user?.first_name || ""} ${job?.user?.last_name || ""}`.trim() || "Customer";
          const initials = customerName.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2);
          const serviceName = job?.service?.name || "Service";
          const price = job?.service?.price;
          const dateStr = job?.date_time_lock?.date;
          const waiting = isWaiting(job?.status);

          return (
            <Box
              key={job.id}
              onClick={() => navigate(`/dashboard/job-done/${job?.id}`)}
              sx={{
                borderRadius: "14px",
                border: "1.5px solid #ebebeb",
                backgroundColor: "white",
                overflow: "hidden",
                cursor: "pointer",
                transition: "all 0.2s ease",
                boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                opacity: waiting ? 1 : 0.92,
                "&:hover": {
                  transform: "translateY(-2px)",
                  boxShadow: "0 8px 24px rgba(0,0,0,0.1)",
                  borderColor: "#4CAF50",
                  opacity: 1,
                },
              }}
            >
              {/* Completed header stripe */}
              <Box
                sx={{
                  px: 3,
                  py: 0.75,
                  background: waiting
                    ? "linear-gradient(90deg, #FF9800 0%, #e65100 100%)"
                    : "linear-gradient(90deg, #2e7d32 0%, #4CAF50 100%)",
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                {!waiting && <MdCheckCircle size={14} color="white" />}
                <Typography sx={{ color: "white", fontSize: "12px", fontWeight: "700", letterSpacing: "0.5px" }}>
                  {waiting ? "AWAITING APPROVAL" : "COMPLETED"}
                </Typography>
              </Box>

              <Stack direction={{ xs: "column", sm: "row" }} sx={{ p: 2.5 }} gap={2}>
                {/* Service image — slightly desaturated for completed look */}
                <Box
                  sx={{
                    width: { xs: "100%", sm: 110 },
                    height: { xs: 140, sm: 110 },
                    borderRadius: "10px",
                    overflow: "hidden",
                    flexShrink: 0,
                    filter: "grayscale(15%)",
                  }}
                >
                  <img
                    src={getServiceLocalImage(serviceName)}
                    alt={serviceName}
                    style={{ width: "100%", height: "100%", objectFit: "cover" }}
                  />
                </Box>

                {/* Main content */}
                <Stack flex={1} spacing={1.5}>
                  {/* Service name + price */}
                  <Stack direction="row" alignItems="flex-start" justifyContent="space-between" gap={1}>
                    <Typography
                      sx={{
                        fontWeight: "700",
                        fontSize: "17px",
                        color: "#1a1a1a",
                        lineHeight: 1.3,
                      }}
                    >
                      {serviceName}
                    </Typography>
                    {price && (
                      <Typography
                        sx={{
                          fontWeight: "800",
                          fontSize: "18px",
                          color: "#2e7d32",
                          flexShrink: 0,
                        }}
                      >
                        ${(+price).toLocaleString()}
                      </Typography>
                    )}
                  </Stack>

                  {/* Info rows */}
                  <Stack spacing={0.75}>
                    {/* Customer */}
                    <Stack direction="row" alignItems="center" gap={1}>
                      <Avatar
                        sx={{
                          width: 24,
                          height: 24,
                          fontSize: "11px",
                          fontWeight: "700",
                          backgroundColor: "#4CAF50",
                          flexShrink: 0,
                        }}
                      >
                        {initials}
                      </Avatar>
                      <Typography sx={{ fontSize: "14px", color: "#444", fontWeight: "500" }}>
                        {customerName}
                      </Typography>
                    </Stack>

                    {/* Email */}
                    {job?.user?.email && (
                      <Stack direction="row" alignItems="center" gap={1}>
                        <MdEmail size={16} color="#999" style={{ flexShrink: 0 }} />
                        <Typography sx={{ fontSize: "13px", color: "#888" }}>
                          {job.user.email}
                        </Typography>
                      </Stack>
                    )}

                    {/* Date */}
                    {dateStr && (
                      <Stack direction="row" alignItems="center" gap={1}>
                        <FaCalendarAlt size={14} color="#999" style={{ flexShrink: 0 }} />
                        <Typography sx={{ fontSize: "13px", color: "#666" }}>
                          {formatDate(dateStr)}
                        </Typography>
                      </Stack>
                    )}

                    {/* Time */}
                    {job?.date_time_lock?.time && (
                      <Stack direction="row" alignItems="center" gap={1}>
                        <MdAccessTime size={16} color="#999" style={{ flexShrink: 0 }} />
                        <Typography sx={{ fontSize: "13px", color: "#888" }}>
                          {job.date_time_lock.time}
                        </Typography>
                      </Stack>
                    )}

                    {/* Location */}
                    {job?.location && (
                      <Stack direction="row" alignItems="center" gap={1}>
                        <MdLocationOn size={16} color="#999" style={{ flexShrink: 0 }} />
                        <Typography
                          sx={{
                            fontSize: "13px",
                            color: "#888",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            maxWidth: "300px",
                          }}
                        >
                          {job.location}
                        </Typography>
                      </Stack>
                    )}
                  </Stack>
                </Stack>

                {/* Right: status + arrow */}
                <Stack
                  alignItems={{ xs: "flex-start", sm: "flex-end" }}
                  justifyContent="space-between"
                  flexShrink={0}
                  gap={1}
                >
                  <Chip
                    label={waiting ? "Pending" : "Completed"}
                    size="small"
                    sx={{
                      backgroundColor: waiting ? "#fff3e0" : "#e8f5e9",
                      color: waiting ? "#e65100" : "#2e7d32",
                      fontWeight: "700",
                      fontSize: "12px",
                      border: `1px solid ${waiting ? "#ffcc80" : "#a5d6a7"}`,
                    }}
                  />
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: "50%",
                      backgroundColor: "#e8f5e910",
                      border: "1px solid #a5d6a7",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <MdArrowForward size={18} color="#4CAF50" />
                  </Box>
                </Stack>
              </Stack>
            </Box>
          );
        })}
      </Stack>
    </Stack>
  );
};

export default CompletedVendorJobsTab;
